import { useState, useEffect } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { CreditCard, Key, Eye, EyeOff, Link } from "lucide-react";

export default function PaymentSettings() {
  
  const [showPayPalSecret, setShowPayPalSecret] = useState(false);
  const [showXenditSecret, setShowXenditSecret] = useState(false);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [xenditEnabled, setXenditEnabled] = useState(false);
  const [showFinanceKey, setShowFinanceKey] = useState(false);
  const [financeForm, setFinanceForm] = useState({ financeApiUrl: "", financeClientId: "", financeApiKey: "" });
  const [financeKeySet, setFinanceKeySet] = useState(false);

  const [paypalForm, setPaypalForm] = useState({
    mode: "sandbox",
    sandboxClientId: "",
    sandboxSecret: "",
    sandboxWebhookId: "",
    liveClientId: "",
    liveSecret: "",
    liveWebhookId: ""
  });

  const [xenditForm, setXenditForm] = useState({
    secretKey: "",
    webhookToken: ""
  });

  const { data, isLoading } = useQuery<any>({
    queryKey: ["/api/payment-settings"]
  });

  const { data: financeData } = useQuery({
    queryKey: ["/api/admin/finance-config"]
  });

  // Update form when data is loaded
  useEffect(() => {
    if (data) {
      setPaypalForm({
        mode: data.paypalMode || "sandbox",
        sandboxClientId: data.paypalSandboxClientId || "",
        sandboxSecret: data.paypalSandboxSecret || "",
        sandboxWebhookId: data.paypalSandboxWebhookId || "",
        liveClientId: data.paypalLiveClientId || "",
        liveSecret: data.paypalLiveSecret || "",
        liveWebhookId: data.paypalLiveWebhookId || ""
      });
      setXenditForm({
        secretKey: data.xenditSecretKey || "",
        webhookToken: data.xenditWebhookToken || ""
      });
      setPaypalEnabled(data.paypalEnabled || false);
      setXenditEnabled(data.xenditEnabled || false);
    }
  }, [data]);

  useEffect(() => {
    if (financeData) {
      setFinanceForm({
        financeApiUrl: (financeData as any).financeApiUrl || "",
        financeClientId: (financeData as any).financeClientId || "",
        financeApiKey: "",
      });
      setFinanceKeySet(!!(financeData as any).financeApiKeySet);
    }
  }, [financeData]);

  const saveMutation = useApiMutation({
    endpoint: "/api/payment-settings",
    method: "PUT",
    invalidate: [["/api/payment-settings"]],
    successMessage: "Payment settings saved successfully",
    errorMessage: (error: any) => error.message || "Failed to save settings",
  });

  const saveFinanceMutation = useApiMutation({
    endpoint: "/api/admin/finance-config",
    method: "PUT",
    invalidate: [["/api/admin/finance-config"]],
    successMessage: "Finance integration settings saved",
    errorMessage: "Failed to save Finance settings",
    onSuccess: (d: any) => setFinanceKeySet(d.financeApiKeySet),
  });

  const toggleMutation = useApiMutation({
    endpoint: (vars: { provider: string }) => `/api/payment-settings/${vars.provider}/toggle`,
    method: "PATCH",
    invalidate: [["/api/payment-settings"]],
    onSuccess: (data: any) => {
      if (data.provider === "paypal") setPaypalEnabled(data.isEnabled);
      if (data.provider === "xendit") setXenditEnabled(data.isEnabled);
      showSuccess(`${data.provider === "paypal" ? "PayPal" : "Xendit"} ${data.isEnabled ? "enabled" : "disabled"}`);
    },
    errorMessage: "Failed to toggle payment method",
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Payment Settings</h1>
        <p className="text-muted-foreground">
          Manage PayPal and Xendit API credentials. All credentials are encrypted in the database.
        </p>
      </div>

      <Tabs defaultValue="paypal" className="space-y-4">
        <TabsList>
          <TabsTrigger value="paypal">
            <CreditCard className="h-4 w-4 mr-2" />
            PayPal
            </TabsTrigger>
            <TabsTrigger value="xendit">
              <Key className="h-4 w-4 mr-2" />
              Xendit
            </TabsTrigger>
            <TabsTrigger value="finance">
              <Link className="h-4 w-4 mr-2" />
              Finance Integration
            </TabsTrigger>
          </TabsList>

          <TabsContent value="paypal">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>PayPal Credentials</CardTitle>
                    <CardDescription>
                      Configure your PayPal API credentials for payment processing
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={paypalEnabled}
                      onCheckedChange={() => toggleMutation.mutate({ provider: "paypal" })}
                      disabled={toggleMutation.isPending}
                    />
                    <Badge variant={paypalEnabled ? "default" : "secondary"}>
                      {paypalEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="paypal-mode">Mode</Label>
                  <select
                    id="paypal-mode"
                    value={paypalForm.mode}
                    onChange={(e) => setPaypalForm({ ...paypalForm, mode: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  >
                    <option value="sandbox">Sandbox (Test)</option>
                    <option value="live">Live (Production)</option>
                  </select>
                </div>

                {/* Sandbox Credentials */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Sandbox Credentials</h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="sandbox-client-id">Sandbox Client ID</Label>
                      <Input
                        id="sandbox-client-id"
                        value={paypalForm.sandboxClientId}
                        onChange={(e) => setPaypalForm({ ...paypalForm, sandboxClientId: e.target.value })}
                        placeholder="Enter Sandbox Client ID"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sandbox-secret">Sandbox Client Secret</Label>
                      <div className="relative">
                        <Input
                          id="sandbox-secret"
                          type={showPayPalSecret ? "text" : "password"}
                          value={paypalForm.sandboxSecret}
                          onChange={(e) => setPaypalForm({ ...paypalForm, sandboxSecret: e.target.value })}
                          placeholder="Enter Sandbox Client Secret"
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full"
                          onClick={() => setShowPayPalSecret(!showPayPalSecret)}
                        >
                          {showPayPalSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="sandbox-webhook-id">Sandbox Webhook ID (Optional)</Label>
                      <Input
                        id="sandbox-webhook-id"
                        value={paypalForm.sandboxWebhookId}
                        onChange={(e) => setPaypalForm({ ...paypalForm, sandboxWebhookId: e.target.value })}
                        placeholder="Enter Sandbox Webhook ID"
                      />
                    </div>
                  </div>
                </div>

                {/* Live Credentials */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Live Credentials</h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="live-client-id">Live Client ID</Label>
                      <Input
                        id="live-client-id"
                        value={paypalForm.liveClientId}
                        onChange={(e) => setPaypalForm({ ...paypalForm, liveClientId: e.target.value })}
                        placeholder="Enter Live Client ID"
                      />
                    </div>
                    <div>
                      <Label htmlFor="live-secret">Live Client Secret</Label>
                      <div className="relative">
                        <Input
                          id="live-secret"
                          type={showPayPalSecret ? "text" : "password"}
                          value={paypalForm.liveSecret}
                          onChange={(e) => setPaypalForm({ ...paypalForm, liveSecret: e.target.value })}
                          placeholder="Enter Live Client Secret"
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full"
                          onClick={() => setShowPayPalSecret(!showPayPalSecret)}
                        >
                          {showPayPalSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="live-webhook-id">Live Webhook ID (Optional)</Label>
                      <Input
                        id="live-webhook-id"
                        value={paypalForm.liveWebhookId}
                        onChange={(e) => setPaypalForm({ ...paypalForm, liveWebhookId: e.target.value })}
                        placeholder="Enter Live Webhook ID"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="xendit">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Xendit Credentials</CardTitle>
                    <CardDescription>
                      Configure your Xendit API credentials for payment processing
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={xenditEnabled}
                      onCheckedChange={() => toggleMutation.mutate({ provider: "xendit" })}
                      disabled={toggleMutation.isPending}
                    />
                    <Badge variant={xenditEnabled ? "default" : "secondary"}>
                      {xenditEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="xendit-secret-key">Secret Key</Label>
                  <div className="relative">
                    <Input
                      id="xendit-secret-key"
                      type={showXenditSecret ? "text" : "password"}
                      value={xenditForm.secretKey}
                      onChange={(e) => setXenditForm({ ...xenditForm, secretKey: e.target.value })}
                      placeholder="Enter Xendit Secret Key"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowXenditSecret(!showXenditSecret)}
                    >
                      {showXenditSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="xendit-webhook-token">Webhook Token (Optional)</Label>
                  <Input
                    id="xendit-webhook-token"
                    value={xenditForm.webhookToken}
                    onChange={(e) => setXenditForm({ ...xenditForm, webhookToken: e.target.value })}
                    placeholder="Enter Xendit Webhook Token"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="finance">
            <Card>
              <CardHeader>
                <CardTitle>Finance Integration</CardTitle>
                <CardDescription>
                  Connect to the Finance app so manual subscription payments are automatically logged as transactions.
                  Get these credentials from the Finance admin after running the register-api-client script.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="finance-api-url">Finance API URL</Label>
                  <Input
                    id="finance-api-url"
                    value={financeForm.financeApiUrl}
                    onChange={(e) => setFinanceForm({ ...financeForm, financeApiUrl: e.target.value })}
                    placeholder="https://finance.yourdomain.com"
                  />
                </div>
                <div>
                  <Label htmlFor="finance-client-id">Client ID</Label>
                  <Input
                    id="finance-client-id"
                    value={financeForm.financeClientId}
                    onChange={(e) => setFinanceForm({ ...financeForm, financeClientId: e.target.value })}
                    placeholder="kmc-app"
                  />
                </div>
                <div>
                  <Label htmlFor="finance-api-key">
                    API Key {financeKeySet && <span className="text-xs text-green-600 ml-2">(currently set)</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      id="finance-api-key"
                      type={showFinanceKey ? "text" : "password"}
                      value={financeForm.financeApiKey}
                      onChange={(e) => setFinanceForm({ ...financeForm, financeApiKey: e.target.value })}
                      placeholder={financeKeySet ? "Leave blank to keep existing key" : "Paste API key from Finance setup script"}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowFinanceKey(!showFinanceKey)}
                    >
                      {showFinanceKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={() => saveFinanceMutation.mutate(financeForm)}
                  disabled={saveFinanceMutation.isPending}
                >
                  {saveFinanceMutation.isPending ? "Saving..." : "Save Finance Settings"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        <div className="flex justify-end">
          <Button 
            onClick={() => {
              const payload: any = {
                paypalMode: paypalForm.mode,
                paypalSandboxClientId: paypalForm.sandboxClientId,
                paypalSandboxWebhookId: paypalForm.sandboxWebhookId,
                paypalLiveClientId: paypalForm.liveClientId,
                paypalLiveWebhookId: paypalForm.liveWebhookId,
                xenditWebhookToken: xenditForm.webhookToken
              };
              
              // Only include secrets if they're not masked
              if (paypalForm.sandboxSecret && !paypalForm.sandboxSecret.includes('•')) {
                payload.paypalSandboxSecret = paypalForm.sandboxSecret;
              }
              if (paypalForm.liveSecret && !paypalForm.liveSecret.includes('•')) {
                payload.paypalLiveSecret = paypalForm.liveSecret;
              }
              if (xenditForm.secretKey && !xenditForm.secretKey.includes('•')) {
                payload.xenditSecretKey = xenditForm.secretKey;
              }
              
              saveMutation.mutate(payload);
            }} 
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving..." : "Save Settings"}
          </Button>
        </div>
    </div>
  );
}
